import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { FeatherAlertTriangle } from "@subframe/core";
import { format } from "date-fns";
import type { ReactNode } from "react";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import type { EventRecordListItemDto } from "@/generated/model/eventRecordListItemDto";
import { categoryColor, type SortDir } from "@/lib/category-display";
import { type EventsHubSortColumn, readAndSignLabel } from "@/lib/events-hub";
import { eventRecordPill } from "@/lib/workspace/status-pill";

interface EventsHubTableProps {
  items: EventRecordListItemDto[];
  activeColumn?: EventsHubSortColumn;
  activeDir: SortDir;
  onSort: (column: EventsHubSortColumn) => void;
  onView: (id: string) => void;
  /** Per-row overflow menu holding the lifecycle actions, rendered beside View. */
  renderRowMenu?: (record: EventRecordListItemDto) => ReactNode;
}

/** "Jane Doe", else their email, else a dash. */
function reportedByLabel(record: EventRecordListItemDto): string {
  if (!record.createdBy) return "—";
  const name =
    `${record.createdBy.firstName ?? ""} ${record.createdBy.lastName ?? ""}`.trim();
  return name || record.createdBy.email;
}

export function EventsHubTable({
  items,
  activeColumn,
  activeDir,
  onSort,
  onView,
  renderRowMenu,
}: EventsHubTableProps) {
  return (
    <Table
      className={TABLE_MIN_WIDTH.lg}
      header={
        <Table.HeaderRow>
          <SortableHeaderCell
            column="event"
            label="Event"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="category"
            label="Category"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          {/* Location is constant across the page, so it is not sortable — the
              list is always scoped to one nursery. */}
          <Table.HeaderCell>Location</Table.HeaderCell>
          <SortableHeaderCell
            column="date"
            label="Date & time"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="reportedBy"
            label="Reported by"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="status"
            label="Status"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell variant="aligned-right">Actions</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {items.map((record) => {
        const categoryTint = categoryColor(
          record.category?.slug ?? undefined,
          record.category?.color ?? undefined,
        );
        const readAndSign = readAndSignLabel(
          record.outstandingAcknowledgementRequestCount,
        );

        return (
          <Table.Row
            key={record.id}
            clickable
            onClick={() => onView(record.id)}
          >
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-3">
                <TintedEntityIcon
                  entity="event"
                  slug={record.category?.slug ?? undefined}
                  color={record.category?.color ?? undefined}
                  fallbackIcon={<FeatherAlertTriangle />}
                />
                <span className="text-body-bold font-body-bold text-neutral-900">
                  {record.eventTypeName}
                </span>
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {record.category ? (
                <span
                  className="text-body-bold font-body-bold"
                  style={categoryTint ? { color: categoryTint } : undefined}
                >
                  {record.category.name}
                </span>
              ) : (
                <span className="text-caption font-caption text-subtext-color">
                  —
                </span>
              )}
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {record.nurseryName || "—"}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {format(new Date(record.submittedAt), "d MMM yyyy, h:mm a")}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {reportedByLabel(record)}
              </span>
            </Table.Cell>
            {/* Status, plus the OTHER reason a row can be in the tab it is in.
                A record with outstanding read-and-sign requests is listed under
                Awaiting acknowledgement as well as under its own status, so
                without this line the tab shows rows reading "In progress" with
                nothing to explain them. */}
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex flex-col items-start gap-0.5">
                <WorkspaceStatusBadge pill={eventRecordPill(record.status)} />
                {readAndSign && (
                  <span className="text-caption font-caption text-subtext-color">
                    {readAndSign}
                  </span>
                )}
              </div>
            </Table.Cell>
            <Table.Cell
              className="h-14 grow shrink-0 basis-0"
              variant="aligned-right"
            >
              <div
                className="flex items-center justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="neutral-primary"
                  onClick={() => onView(record.id)}
                >
                  View
                </Button>
                {renderRowMenu?.(record)}
              </div>
            </Table.Cell>
          </Table.Row>
        );
      })}
    </Table>
  );
}
